import { createContext, useContext } from 'react';

import PropTypes from 'prop-types';

import { useContactLists } from '#contactListFeature/models/queries';

import { useUpdatePostMailingCampaign, useSyncPostMailingCampaignLists } from '../models/campaigns/mutations';
import { usePostMailingCampaign } from '../models/campaigns/queries';
import { usePostMailingTemplates } from '../models/postMailingTemplates/queries';

const PostMailingDetailsContext = createContext(null);

export const usePostMailingDetailsContext = () => {
	const context = useContext(PostMailingDetailsContext);
	if (!context) {
		throw new Error('usePostMailingDetailsContext must be used within PostMailingDetailsProvider');
	}
	return context;
};

/**
 * Extracts the contact list IDs currently assigned to the campaign.
 *
 * @param {Array<{contactListId?: string, contactLists?: {id: string}}>} selectedLists
 * @returns {string[]}
 */
const toSelectedIds = (selectedLists) =>
	selectedLists.map((entry) => entry.contactListId ?? entry.contactLists?.id).filter(Boolean);

export const PostMailingDetailsProvider = ({ children, campaignId }) => {
	const { data: campaign, isLoading: loadingCampaign, refetch: refetchCampaign } = usePostMailingCampaign(campaignId);
	const { data: contactLists = [], isLoading: loadingContactLists } = useContactLists();
	const { data: templates = [], isLoading: loadingTemplates } = usePostMailingTemplates();

	const updateCampaignMutation = useUpdatePostMailingCampaign();
	const syncListsMutation = useSyncPostMailingCampaignLists();

	const selectedLists = campaign?.postMailingCampaign_contactList ?? [];

	const handleRename = async (name) => {
		await updateCampaignMutation.mutateAsync({ id: campaignId, updates: { name } });
	};

	const handleTemplateChange = async (templateId) => {
		// Re-link the campaign to another template; the design is read live from it.
		await updateCampaignMutation.mutateAsync({
			id: campaignId,
			updates: { postMailingTemplateId: templateId },
		});
	};

	const handleListToggle = async (listId) => {
		const currentIds = toSelectedIds(selectedLists);
		const nextIds = currentIds.includes(listId)
			? currentIds.filter((id) => id !== listId)
			: [...currentIds, listId];
		await syncListsMutation.mutateAsync({ campaignId, contactListIds: nextIds });
	};

	const contextValue = {
		campaign,
		loadingCampaign,
		contactLists,
		loadingContactLists,
		templates,
		loadingTemplates,
		selectedLists,
		selectedListIds: toSelectedIds(selectedLists),
		handleRename,
		handleTemplateChange,
		handleListToggle,
		refetchCampaign,
	};

	return <PostMailingDetailsContext.Provider value={contextValue}>{children}</PostMailingDetailsContext.Provider>;
};

PostMailingDetailsProvider.propTypes = {
	children: PropTypes.node.isRequired,
	campaignId: PropTypes.string.isRequired,
};
